import { FC, useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Container } from '@/components/ui/Container';
import { faqApi } from '@/services/api';
import { FAQ } from '@/types';

export const FAQManagement: FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState<string[]>([]);

  // 获取FAQ列表
  const fetchFAQs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await faqApi.getAdminFAQList({
        search: searchTerm || undefined,
        category: selectedCategory || undefined,
        page: 1,
        size: 100 // 先简单处理，获取所有数据
      });
      
      setFaqs(response.items || []); // 确保faqs不会是null
    } catch (err) {
      console.error('Failed to fetch FAQs:', err);
      setError('获取FAQ列表失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 获取分类列表
  const fetchCategories = async () => {
    try {
      const cats = await faqApi.getFAQCategories();
      setCategories(cats || []); // 确保categories不会是null
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setCategories([]); // 错误时设置为空数组
    }
  };

  useEffect(() => {
    fetchFAQs();
    fetchCategories();
  }, [searchTerm, selectedCategory]);

  // 删除FAQ
  const handleDeleteFaq = async (id: string) => {
    if (!confirm('确定要删除这个FAQ吗？')) return;
    
    try {
      await faqApi.deleteFAQ(id);
      await fetchFAQs(); // 重新获取列表
    } catch (err) {
      console.error('Failed to delete FAQ:', err);
      alert('删除FAQ失败，请稍后重试');
    }
  };

  // 切换FAQ状态
  const handleToggleStatus = async (faq: FAQ) => {
    try {
      await faqApi.updateFAQ(faq.id, { is_active: !faq.is_active });
      await fetchFAQs(); // 重新获取列表
    } catch (err) {
      console.error('Failed to toggle FAQ status:', err);
      alert('更新FAQ状态失败，请稍后重试');
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="py-8">
          <div className="text-center">
            <p className="text-gray-600">加载中...</p>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="py-8">
        {/* 页面标题和操作 */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">FAQ管理</h1>
            <p className="text-gray-600 mt-2">管理网站常见问题</p>
          </div>
          <Button
            onClick={() => setShowCreateForm(true)}
            className="bg-primary-600 text-white hover:bg-primary-700"
          >
            + 新建FAQ
          </Button>
        </div>

        {/* 搜索和筛选 */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="搜索FAQ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">所有分类</option>
            {(categories || []).map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <p className="text-red-800">{error}</p>
            <Button
              onClick={fetchFAQs}
              className="mt-2 text-sm bg-red-600 text-white hover:bg-red-700"
            >
              重试
            </Button>
          </div>
        )}

        {/* FAQ列表 */}
        <div className="space-y-4">
          {faqs.length === 0 ? (
            <Card>
              <div className="text-center py-8">
                <p className="text-gray-500">暂无FAQ数据</p>
              </div>
            </Card>
          ) : (
            (faqs || []).map((faq) => (
              <Card key={faq.id}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                        {faq.category}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded ${
                        faq.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {faq.is_active ? '已启用' : '已禁用'}
                      </span>
                      <span className="text-xs text-gray-500">
                        查看次数: {faq.view_count}
                      </span>
                      <span className="text-xs text-gray-500">
                        排序: {faq.sort_order}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {faq.question}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {faq.answer}
                    </p>
                    <div className="mt-2 text-xs text-gray-400">
                      创建: {new Date(faq.created_at).toLocaleDateString()}
                      {faq.updated_at !== faq.created_at && (
                        <> | 更新: {new Date(faq.updated_at).toLocaleDateString()}</>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <Button
                      onClick={() => setEditingFaq(faq)}
                      className="text-sm bg-blue-600 text-white hover:bg-blue-700"
                    >
                      编辑
                    </Button>
                    <Button
                      onClick={() => handleToggleStatus(faq)}
                      className={`text-sm ${
                        faq.is_active
                          ? 'bg-yellow-600 hover:bg-yellow-700'
                          : 'bg-green-600 hover:bg-green-700'
                      } text-white`}
                    >
                      {faq.is_active ? '禁用' : '启用'}
                    </Button>
                    <Button
                      onClick={() => handleDeleteFaq(faq.id)}
                      className="text-sm bg-red-600 text-white hover:bg-red-700"
                    >
                      删除
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* 创建FAQ表单模态框 */}
        {showCreateForm && (
          <FAQFormModal
            onClose={() => setShowCreateForm(false)}
            onSuccess={() => {
              setShowCreateForm(false);
              fetchFAQs();
              fetchCategories();
            }}
          />
        )}

        {/* 编辑FAQ表单模态框 */}
        {editingFaq && (
          <FAQFormModal
            faq={editingFaq}
            onClose={() => setEditingFaq(null)}
            onSuccess={() => {
              setEditingFaq(null);
              fetchFAQs();
              fetchCategories();
            }}
          />
        )}
      </div>
    </Container>
  );
};

// FAQ表单模态框组件
interface FAQFormModalProps {
  faq?: FAQ;
  onClose: () => void;
  onSuccess: () => void;
}

const FAQFormModal: FC<FAQFormModalProps> = ({ faq, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    category: faq?.category || '',
    question: faq?.question || '',
    answer: faq?.answer || '',
    keywords: faq?.keywords ? JSON.parse(faq.keywords) : [],
    sort_order: faq?.sort_order || 0,
    is_active: faq?.is_active ?? true
  });
  const [keywordInput, setKeywordInput] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.category || !formData.question || !formData.answer) {
      alert('请填写所有必填字段');
      return;
    }

    setLoading(true);
    try {
      if (faq) {
        // 编辑模式
        await faqApi.updateFAQ(faq.id, {
          category: formData.category,
          question: formData.question,
          answer: formData.answer,
          keywords: formData.keywords,
          sort_order: formData.sort_order,
          is_active: formData.is_active
        });
      } else {
        // 创建模式
        await faqApi.createFAQ({
          category: formData.category,
          question: formData.question,
          answer: formData.answer,
          keywords: formData.keywords,
          sort_order: formData.sort_order,
          is_active: formData.is_active
        });
      }
      onSuccess();
    } catch (err) {
      console.error('Failed to save FAQ:', err);
      alert('保存FAQ失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()]
      }));
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter((k: string) => k !== keyword)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">
            {faq ? '编辑FAQ' : '创建FAQ'}
          </h2>
          <Button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              分类 *
            </label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="例如：购买相关"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              问题 *
            </label>
            <input
              type="text"
              value={formData.question}
              onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="输入常见问题"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              答案 *
            </label>
            <textarea
              value={formData.answer}
              onChange={(e) => setFormData(prev => ({ ...prev, answer: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="输入问题答案"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              关键词
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="输入关键词"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
              />
              <Button
                type="button"
                onClick={addKeyword}
                className="bg-gray-600 text-white hover:bg-gray-700"
              >
                添加
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(formData.keywords || []).map((keyword: string) => (
                <span
                  key={keyword}
                  className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded flex items-center gap-1"
                >
                  {keyword}
                  <button
                    type="button"
                    onClick={() => removeKeyword(keyword)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                排序值
              </label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                状态
              </label>
              <select
                value={formData.is_active ? 'active' : 'inactive'}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'active' }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="active">启用</option>
                <option value="inactive">禁用</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              className="bg-gray-300 text-gray-700 hover:bg-gray-400"
            >
              取消
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-primary-600 text-white hover:bg-primary-700 disabled:bg-primary-400"
            >
              {loading ? '保存中...' : '保存'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};